import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import AnimatedGlow, { type GlowEvent, type PresetConfig } from 'react-native-animated-glow';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../hooks/useTheme';

interface Props {
  active: boolean;
}

// Brand palette constants — keep in sync with theme/index.ts
const ELECTRIC_BLUE = '#00D9FF';
const ORANGE        = '#EE6C4C';  // light-mode primary
const SKY_BLUE      = '#63B8FF';  // dark-mode primary

const getOverlayPreset = (isDarkMode: boolean): PresetConfig => ({
  metadata: {
    name: 'AI Glow Border',
    textColor: '#ffffff',
    category: 'Custom',
    tags: ['ai', 'brand', 'inset'],
  },
  states: [
    {
      name: 'default',
      preset: {
        // Android should render full-bleed to the viewport edge.
        // A large corner radius makes the glow look inset/clipped there.
        cornerRadius: Platform.OS === 'android' ? 0 : 28,
        // Dark: electric-blue → sky-blue warm/cool cycle (matches Apple Intelligence rhythm)
        // Light: orange → electric-blue warm/cool cycle
        outlineWidth: isDarkMode ? 3.2 : 3.0,
        borderColor: isDarkMode
          ? [ELECTRIC_BLUE, SKY_BLUE, '#00AAEE', ELECTRIC_BLUE]
          : [ORANGE, '#FF9A6C', ELECTRIC_BLUE, ORANGE],
        backgroundColor: 'transparent',
        animationSpeed: 1.1,
        borderSpeedMultiplier: 0.9,
        glowLayers: [
          {
            // Ambient halo behind the border line
            glowPlacement: 'behind',
            colors: isDarkMode
              ? [ELECTRIC_BLUE, SKY_BLUE, '#0090CC']
              : [ORANGE, '#FF8050', ELECTRIC_BLUE],
            glowSize: 20,
            opacity: isDarkMode ? 0.32 : 0.26,
            speedMultiplier: 0.85,
            coverage: 1,
            relativeOffset: 0,
          },
          {
            // Inner rim glow — adds depth on the inward face
            glowPlacement: 'inside',
            colors: isDarkMode
              ? ['#7DDFFF', '#A8D8FF']
              : ['#FF9E7A', '#7DDFFF'],
            glowSize: 3,
            opacity: isDarkMode ? 0.35 : 0.30,
            speedMultiplier: 0.9,
            coverage: 1,
            relativeOffset: 0,
          },
          {
            // Bright travelling sweep — the primary "Apple Intelligence" shimmer
            glowPlacement: 'over',
            colors: isDarkMode
              ? [ELECTRIC_BLUE, '#FFFFFF', SKY_BLUE]
              : ['#FF7A5A', '#FFFFFF', ELECTRIC_BLUE],
            glowSize: [0, 9, 9, 0],
            opacity: isDarkMode ? 0.72 : 0.65,
            speedMultiplier: 1.75,
            coverage: 0.32,
            relativeOffset: 0.05,
          },
          {
            // Bright white sparkle trailing the sweep
            glowPlacement: 'over',
            colors: ['#FFFFFF', '#E8F8FF'],
            glowSize: [0, 3.5, 3.5, 0],
            opacity: isDarkMode ? 0.60 : 0.50,
            speedMultiplier: 2.0,
            coverage: 0.18,
            relativeOffset: 0.20,
          },
        ],
      },
    },
    {
      name: 'hover',
      transition: 460,
      preset: {
        outlineWidth: isDarkMode ? 3.8 : 3.5,
        borderSpeedMultiplier: 1.2,
        glowLayers: [
          { opacity: isDarkMode ? 0.40 : 0.32, glowSize: 24, speedMultiplier: 0.92 },
          { opacity: isDarkMode ? 0.42 : 0.36, glowSize: 3.5, speedMultiplier: 0.92 },
          { opacity: isDarkMode ? 0.82 : 0.74, coverage: 0.38, speedMultiplier: 2.0 },
          { opacity: isDarkMode ? 0.70 : 0.60, coverage: 0.22, speedMultiplier: 2.25 },
        ],
      },
    },
  ],
});

export default function AIGlowBorder({ active }: Props) {
  const glowSupported = Platform.OS !== 'android';
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [visible, setVisible] = useState(false);
  const [showGlow, setShowGlow] = useState(false);
  const [glowState, setGlowState] = useState<GlowEvent>('default');
  const pulseTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const preset = useMemo(() => getOverlayPreset(theme.dark), [theme.dark]);
  const fullscreenBleedStyle = useMemo(
    () => ({
      top: -Math.max(0, insets.top),
      bottom: -Math.max(0, insets.bottom),
      left: -Math.max(0, insets.left),
      right: -Math.max(0, insets.right),
    }),
    [insets.bottom, insets.left, insets.right, insets.top]
  );

  useEffect(() => {
    if (!glowSupported) return;
    if (active) {
      setVisible(true);
      const showTimer = setTimeout(() => setShowGlow(true), 16);
      setGlowState('default');
      pulseTimerRef.current = setInterval(() => {
        setGlowState((prev) => (prev === 'default' ? 'hover' : 'default'));
      }, 760);
      return () => clearTimeout(showTimer);
    }
    if (pulseTimerRef.current) {
      clearInterval(pulseTimerRef.current);
      pulseTimerRef.current = null;
    }
    setShowGlow(false);
    setGlowState('default');
    const hideTimer = setTimeout(() => {
      setVisible(false);
    }, 220);
    return () => clearTimeout(hideTimer);
  }, [active, glowSupported]);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) {
        clearInterval(pulseTimerRef.current);
      }
    };
  }, []);

  // Rendered as a plain absolute View — NOT a Modal — so that pointerEvents="none"
  // is fully reliable on iOS. Using a Modal (even with pointerEvents="none" on its
  // content) creates a second UIWindow on iOS that can intercept touches on newer
  // hardware / iOS 18, blocking controls underneath. A regular absolute View with
  // pointerEvents="none" is guaranteed to pass all touches through.
  if (!glowSupported || !visible) return null;
  return (
    <View style={[styles.fill, fullscreenBleedStyle]} pointerEvents="none">
      <AnimatedGlow
        preset={preset}
        style={styles.glowFrame}
        isVisible={showGlow}
        activeState={glowState}
      >
        <View style={styles.innerFrame} />
      </AnimatedGlow>
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    ...StyleSheet.absoluteFillObject,
  },
  glowFrame: {
    ...StyleSheet.absoluteFillObject,
  },
  innerFrame: {
    ...StyleSheet.absoluteFillObject,
  },
});
